// AMC'S ABC'S of AI — Book content
// Each entry represents a "page" in the book. Use HTML strings for rich rendering.
// Chapters are grouped and referenced from the Index.

export const BOOK_META = {
  title: "AMC'S ABC'S of AI",
  subtitle: "From AMC inNOVAtions",
  edition: "First Edition",
  tagline: "A friendly, plain-English guide to building apps with AI — for humans who've never written a line of code.",
};

// Utility HTML helpers used inside content strings:
// <div class="callout-tip">...</div>
// <div class="callout-warn">...</div>
// <pre><code>...</code></pre>

export const CHAPTERS = [
  {
    id: "real-cost",
    number: "Ch. 19",
    title: "The Real Cost of Starting an AI Business",
    summary: "The money conversation nobody else is having.",
    content: `
      <p>Let&apos;s have the conversation nobody else is having. Yes, you can technically start on free tiers. No, you probably can&apos;t finish there.</p>
      <h3>What&apos;s genuinely free</h3>
      <ul><li>AI chat tools free tiers.</li><li>GitHub free for solo builders.</li><li>Builder platform starter credits.</li><li>YouTube + docs + communities.</li></ul>
      <h3>What quietly costs money</h3>
      <ul><li><strong>Builder credits</strong> — every message burns tokens.</li><li><strong>Hosting</strong> $5–$20/mo once live.</li><li><strong>Domain</strong> $10–$15/year — non-negotiable.</li><li><strong>Stripe</strong> ~2.9% + 30¢ per sale.</li><li><strong>Email</strong> free at launch.</li><li><strong>Database</strong> $0–$25/mo.</li></ul>
      <h3>Realistic first launch</h3>
      <p>Up-front <strong>$100–$400</strong>, then <strong>$20–$100/month</strong> live.</p>
      <h3>The honest truth about earnings</h3>
      <p>Most first apps make <strong>zero in month one</strong>. That is normal. Month 3: sales trickle. Month 6: small steady income or a clear reason why not.</p>
      <div class="callout-tip"><strong>Rule of thumb:</strong> budget as if launch earns nothing. If it does, delighted. If not, you&apos;ve bought an education worth many times the money.</div>
      <h3>Keep costs low</h3>
      <ul><li>Ship the smallest thing that could possibly be paid for.</li><li>Charge day one. Even $5.</li><li>Talk to five real people before spending &gt; $100.</li><li>Turn off anything you&apos;re not using.</li></ul>
    `,
  },
  {
    id: "where-to-spend",
    number: "Ch. 20",
    title: "Where To Spend The Money — The Too-Many-Baskets Trap",
    summary: "The single biggest silent killer of solo builders.",
    content: `
      <h3>The Free-Tier Trap</h3>
      <p>Here&apos;s how it usually plays out. You start on Platform A, the free credits run out, and you can&apos;t quite afford the subscription — so you decide to jump ship to Platform B. Except you can&apos;t, not really. Moving a half-built app to a different platform is like moving a half-cooked lasagna to a different oven: it doesn&apos;t survive the trip, and you almost always end up starting from scratch. You lose the code, you lose the momentum, and you lose weeks of your life. Six months in, you&apos;ve got four unfinished apps, three subscriptions quietly bleeding you dry, and no product to show for any of it.</p>
      <div class="callout-warn"><strong>This is the biggest silent killer of solo builders.</strong> Not lack of skill. Fragmentation.</div>
      <h3>Pick One Home And Commit</h3>
      <p>Try two or three free tiers. Pick the one that fits your brain. <strong>Stop shopping.</strong> Put your money into that one place.</p>
      <blockquote>One platform, one project, until it earns its first dollar.</blockquote>
      <h3>Where your first $100 goes</h3>
      <ol><li>Builder subscription $20–$40.</li><li>Domain $12/year — buy the day you name the product.</li><li>Smallest hosting plan $0–$20/mo.</li><li>Free email tier.</li><li>Nothing else until earning.</li></ol>
      <h3>What NOT to spend on <strong class="neon-magenta">YET</strong></h3>
      <p><em>Not never — just not yet.</em></p>
      <ul><li>Courses — YouTube is free and often better.</li><li>Ads — advertising an untested product is lighting money on fire.</li><li>Logo designers — AI does it for pennies.</li><li>Business coaches / AI-money gurus — if they were making money from AI, they wouldn&apos;t need yours.</li></ul>
      <div class="callout-tip"><strong>One home. One project. Finish it.</strong> Even &quot;small but earning a tiny bit&quot; is the crack of daylight everything grows through.</div>
    `,
  },
  {
    id: "welcome",
    number: "Ch. 1",
    title: "Welcome — Why This Book Exists",
    summary: "The whole point of the book, and why you're the right person to read it.",
    content: `
      <p>Hello, and welcome. If you've ever looked at a phone app or a website and thought, <em>"I could probably use one of these for something I do"</em> — but never dreamed you could actually make one — this book is for you.</p>
      <p>AI has quietly changed the rules. You don't need a computer-science degree. You don't need to memorise a hundred keyboard shortcuts. You need <span class="neon-cyan">curiosity</span>, a bit of patience, and the willingness to ask questions in plain English. That's it.</p>
      <h3>Who this book is for</h3>
      <ul>
        <li>People who feel their computer skills stopped somewhere around "attaching a file to an email."</li>
        <li>Small-business owners with an idea for a tool that would save them time.</li>
        <li>Creatives, tradies, teachers, parents — anyone who's had a "wouldn't it be nice if…" moment.</li>
      </ul>
      <h3>How to read it</h3>
      <p>Like a real book, cover-to-cover if you want, or dip into any chapter that catches your eye from the Table of Contents. Every page has a little sticky-note button in the corner — use it to leave yourself reminders as you go. They save automatically, right here in your browser.</p>
      <div class="callout-tip">
        <strong class="neon-cyan">Tip:</strong> There are no stupid questions in this book. If a term looks unfamiliar, jump to the Glossary at the end. Come back when you're ready.
      </div>
      <p>Take a breath. You've got this.</p>
    `,
  },
  {
    id: "meet-ai",
    number: "Ch. 2",
    title: "Meet AI — Your New Building Partner",
    summary: "What AI actually is (and isn't), and how to think about working with it.",
    content: `
      <p>Think of an AI assistant as a very clever, very fast, very patient junior helper. It has read most of the internet, remembers a huge amount of it, and will happily do the boring bits for you — <em>if</em> you tell it clearly what you want.</p>
      <h3>What it is good at</h3>
      <ul>
        <li>Turning your plain-English idea into working code.</li>
        <li>Explaining unfamiliar words, files, or errors.</li>
        <li>Doing the repetitive, fiddly setup work that used to take hours.</li>
      </ul>
      <h3>What it is not</h3>
      <ul>
        <li>A mind-reader. It only knows what you tell it.</li>
        <li>Perfect. It can invent things confidently. Always check important results.</li>
        <li>A replacement for your judgement. You are still the boss of the project.</li>
      </ul>
      <div class="callout-tip"><strong>Golden rule:</strong> Treat the AI like a helpful new employee on their first day. Explain the goal, give context, and check the work.</div>
      <blockquote>"AI won't take your job. A person who knows how to use AI might." — internet folk-wisdom, mostly true.</blockquote>
    `,
  },
  {
    id: "what-is-an-app",
    number: "Ch. 3",
    title: "What Even Is An App?",
    summary: "Frontend, backend, database — the three friends that make everything work.",
    content: `
      <p>Every app you've ever used — Instagram, your banking app, this book — is basically three things politely working together.</p>
      <h3>1. The Frontend (what you see)</h3>
      <p>Buttons, colours, pages, text boxes. Anything visible or tap-able. Runs inside your browser or phone. Built mostly with HTML, CSS, and JavaScript.</p>
      <h3>2. The Backend (what you don't see)</h3>
      <p>The engine room. Handles logins, calculations, saving data, sending emails. Runs on a computer somewhere on the internet, called a <em>server</em>.</p>
      <h3>3. The Database (the memory)</h3>
      <p>Where all the app's information lives — users, posts, orders, your sticky notes. Think of it as a very well-organised filing cabinet.</p>
      <pre><code>[ You ]  ⇄  [ Frontend ]  ⇄  [ Backend ]  ⇄  [ Database ]
   phone         browser         server         filing
                                                cabinet</code></pre>
      <p>When you tap a button, a message flows across those arrows and something happens. That's the whole magic trick.</p>
      <div class="callout-tip"><strong>Why this matters:</strong> When something breaks, knowing <em>which</em> of the three is unhappy makes fixing it ten times faster.</div>
    `,
  },
  {
    id: "code-languages",
    number: "Ch. 4",
    title: "A Friendly Tour of Code Languages",
    summary: "Just enough to understand what the AI is writing on your behalf.",
    content: `
      <p>You don't need to <em>write</em> any of these. You just need to recognise them when you see them, the way you recognise French vs Spanish without speaking either.</p>
      <h3>HTML <span class="text-slate-500 text-sm">(HyperText Markup Language)</span> — the skeleton</h3>
      <p>Describes the parts of a page. Headings, paragraphs, buttons, images.</p>
      <pre><code>&lt;h1&gt;Hello world&lt;/h1&gt;
&lt;p&gt;This is a paragraph.&lt;/p&gt;</code></pre>
      <h3>CSS <span class="text-slate-500 text-sm">(Cascading Style Sheets)</span> — the clothing</h3>
      <p>Colours, fonts, spacing, layout. Makes things pretty.</p>
      <pre><code>h1 { color: cyan; font-size: 48px; }</code></pre>
      <h3>JavaScript <span class="text-slate-500 text-sm">(often shortened to JS)</span> — the muscles</h3>
      <p>Makes things move, react to clicks, fetch data. The most common language in the world.</p>
      <pre><code>button.addEventListener("click", () =&gt; alert("Hi!"));</code></pre>
      <h3>Python <span class="text-slate-500 text-sm">(named after Monty Python, not the snake)</span> — the brain</h3>
      <p>Beloved for backends, data work, and anything involving AI. Reads almost like English.</p>
      <pre><code>if user.is_logged_in:
    send_welcome_email(user.email)</code></pre>
      <h3>SQL <span class="text-slate-500 text-sm">(Structured Query Language, pronounced &quot;sequel&quot;)</span> — the librarian</h3>
      <p>Asks the database questions in a very polite way.</p>
      <pre><code>SELECT name FROM users WHERE country = 'Australia';</code></pre>
      <div class="callout-tip"><strong>Reassurance:</strong> The AI writes these. You review them. If a line looks scary, ask the AI: "explain this line like I'm five."</div>
    `,
  },
  {
    id: "prompting",
    number: "Ch. 5",
    title: "The Art of Prompting",
    summary: "How to talk to AI so it builds what you actually meant.",
    content: `
      <p>Prompting is just <em>asking well</em>. The clearer you are, the better the result. Vague in, vague out.</p>
      <h3>The recipe for a great prompt</h3>
      <ol>
        <li><strong>Context</strong> — what are we building and why?</li>
        <li><strong>Goal</strong> — what should happen when this is done?</li>
        <li><strong>Constraints</strong> — must-haves and must-nots.</li>
        <li><strong>Examples</strong> — a picture, a link, a similar app.</li>
      </ol>
      <h3>Weak vs strong prompt</h3>
      <blockquote>❌ "Make me a login page."</blockquote>
      <blockquote>✅ "Add a login page that accepts email + password, shows friendly errors, and remembers the user for 30 days. Match the dark cyan theme we're already using."</blockquote>
      <div class="callout-warn"><strong>Watch out:</strong> If you ask for too many things at once, the AI can lose track. Ship one small thing, test it, then ask for the next.</div>
      <h3>Tiny phrases that punch above their weight</h3>
      <ul>
        <li>"Explain your plan before writing code."</li>
        <li>"Show me the smallest possible change."</li>
        <li>"What could go wrong here?"</li>
        <li>"Rewrite this so a beginner can follow."</li>
      </ul>
    `,
  },
  {
    id: "first-build",
    number: "Ch. 6",
    title: "Your First Build — Step by Step",
    summary: "A walk-through of your very first AI-assisted app.",
    content: `
      <p>Let's imagine you want a simple <strong>Shopping List</strong> app. Here's the whole flow, start to finish.</p>
      <h3>Step 1 — Describe it in one paragraph</h3>
      <blockquote>"An app where I type items, press enter, and they appear as a list. I can tick them off, and cross them out. Notes save so I don't lose them if I close the app."</blockquote>
      <h3>Step 2 — Let the AI plan first</h3>
      <p>Ask it: <em>"Before writing any code, explain in plain English how you'll build this and what the pieces will be."</em> Read the plan. Push back if anything looks wrong.</p>
      <h3>Step 3 — Build one slice at a time</h3>
      <ol>
        <li>Just the input box that adds items to a list. Test it.</li>
        <li>Add the tick-to-cross-off feature. Test it.</li>
        <li>Add "save so it survives a refresh." Test it.</li>
      </ol>
      <h3>Step 4 — Test as you go</h3>
      <p>Click every button. Try weird inputs. Empty text. Very long text. Emoji. If it doesn't crash — brilliant.</p>
      <h3>Step 5 — Save your progress</h3>
      <p>Use GitHub (Chapter 9) or your platform's rollback feature. Never lose a good version.</p>
      <div class="callout-tip"><strong>Reality check:</strong> Your first build will feel messy. That's normal. Ship it anyway. You'll learn more from one finished ugly app than ten unfinished pretty ones.</div>
    `,
  },
  {
    id: "data-storage",
    number: "Ch. 7",
    title: "Data — Where Your App Remembers Things",
    summary: "Databases explained without the jargon.",
    content: `
      <p>Every app remembers things. Your name, your login, your last order, your sticky notes. That "memory" is called a <strong>database</strong>.</p>
      <h3>Two flavours you'll hear about</h3>
      <ul>
        <li><strong>SQL databases</strong> (Postgres, MySQL): like a spreadsheet with strict rules about which columns hold what. Great for orders, users, invoices.</li>
        <li><strong>NoSQL databases</strong> (MongoDB, Firestore): more like a big filing cabinet of freeform documents. Great for flexible, changing data.</li>
      </ul>
      <h3>What lives in a database?</h3>
      <p>Anything you want the app to remember after you close it. Users, messages, uploads, settings.</p>
      <h3>What does NOT belong in a database?</h3>
      <ul>
        <li>Passwords in plain text — always <em>hash</em> them (a one-way scramble).</li>
        <li>Giant files like videos — store those in <em>object storage</em>, keep only the address in the database.</li>
        <li>Secrets and API keys — those go in environment variables (Chapter 8).</li>
      </ul>
      <pre><code>{
  "id": "usr_882",
  "name": "Sam",
  "created_at": "2026-02-14T09:12:00Z",
  "email": "sam@example.com"
}</code></pre>
      <p>That's what one entry (called a "document" or "row") looks like. Not scary. Just a labelled parcel of information.</p>
    `,
  },
  {
    id: "local-vs-online",
    number: "Ch. 8",
    title: "Local vs Online — Where Stuff Lives",
    summary: "The difference between your computer, the browser, and 'the cloud'.",
    content: `
      <h3>Local (on your machine or your browser)</h3>
      <p>Data lives only on the specific device you used. Fast, private, free. But if you switch phones — it's gone.</p>
      <p>Examples: browser <code>localStorage</code> (which is where the sticky notes in this app live), files on your desktop, a Word document.</p>
      <h3>Online (in the cloud)</h3>
      <p>Data lives on servers owned by companies like Amazon, Google, Microsoft. Accessible from any device, backed up, shareable. Costs a small amount of money.</p>
      <p>Examples: Gmail, iCloud photos, your bank statements, most modern apps.</p>
      <h3>Which should you use?</h3>
      <div class="callout-tip">
        <strong>Rule of thumb:</strong> if the user would be upset when they lose the data, put it online. If it's a scratchpad or preference, local is fine (and cheaper).
      </div>
      <h3>"The Cloud" demystified</h3>
      <p>The cloud is not fluffy or mysterious. It's just <em>someone else's computer</em> that you rent by the hour. When you "deploy" an app, you're basically saying "please run my code on your computer so people can visit it."</p>
      <pre><code>Your laptop        ─►   dev / testing (local)
Your GitHub        ─►   the safe copy
Hosted server      ─►   the live app (online)</code></pre>
    `,
  },
  {
    id: "github",
    number: "Ch. 9",
    title: "GitHub — Your Safety Net",
    summary: "Why every builder uses it, and how to think about it.",
    content: `
      <p>Imagine writing a novel with no <em>undo</em> button. Terrifying. GitHub is the undo button for your entire project — and a lot more.</p>
      <h3>What it actually does</h3>
      <ul>
        <li><strong>Backs up</strong> every version of your code online.</li>
        <li><strong>Lets you rewind</strong> to any earlier version if today's changes broke everything.</li>
        <li><strong>Lets others collaborate</strong> without stepping on each other's toes.</li>
        <li><strong>Shows what changed</strong> between versions, line by line.</li>
      </ul>
      <h3>Three words you'll see a lot</h3>
      <ul>
        <li><strong>Commit</strong> — "save a snapshot of the project right now, with a short note about what changed."</li>
        <li><strong>Push</strong> — "send my local snapshots up to GitHub."</li>
        <li><strong>Pull</strong> — "grab the latest snapshots from GitHub down to my machine."</li>
      </ul>
      <div class="callout-warn"><strong>Don't skip this:</strong> Connect your project to GitHub <em>before</em> you build anything important. Future-you will send a thank-you card.</div>
      <h3>Rhythm</h3>
      <p>Small changes → test → commit → push. Repeat. It becomes second nature within a week.</p>
    `,
  },
  {
    id: "debugging",
    number: "Ch. 10",
    title: "Debugging — When Things Break (And They Will)",
    summary: "How to stay calm when the screen goes red.",
    content: `
      <p>Errors are not failure. Errors are the app telling you, in its slightly grumpy way, exactly where it's confused. Read them.</p>
      <h3>The three-line rule</h3>
      <p>Most useful information is in the top three lines of an error. The rest is usually noise. Copy those three lines and paste them straight to the AI with the question: <em>"What does this mean, and what should I try first?"</em></p>
      <h3>The debugging mindset</h3>
      <ol>
        <li><strong>Reproduce.</strong> Make the bug happen again on purpose.</li>
        <li><strong>Isolate.</strong> Change one thing at a time.</li>
        <li><strong>Ask.</strong> If stuck for 15 minutes, ask the AI. Include the error, the file, and what you were doing.</li>
        <li><strong>Rollback.</strong> If you've dug too deep, roll back to your last working version and try a different approach.</li>
      </ol>
      <h3>Common early errors</h3>
      <ul>
        <li><code>undefined is not a function</code> — you tried to use something that doesn't exist. Usually a typo.</li>
        <li><code>CORS</code> errors — your frontend and backend haven't been formally introduced. Add the site to the allowed list.</li>
        <li><code>404 Not Found</code> — the address you asked for doesn't exist. Check spelling and paths.</li>
        <li><code>500 Internal Server Error</code> — the backend fell over. Look at the server logs.</li>
      </ul>
      <div class="callout-tip"><strong>Take a walk.</strong> Seriously. 90% of bugs solve themselves once you get up from the chair for five minutes.</div>
    `,
  },
  {
    id: "testing",
    number: "Ch. 11",
    title: "When To Test And What To Check",
    summary: "A friendly checklist so bugs don't surprise your users.",
    content: `
      <p>Testing is just <em>using the app on purpose to see if it lies to you</em>. You already do this instinctively. Let's make it deliberate.</p>
      <h3>Test after every meaningful change</h3>
      <p>Not after every keystroke. After every <em>feature</em>. Small, frequent tests catch bugs early — when they're still cheap to fix.</p>
      <h3>The five checks that catch 80% of problems</h3>
      <ol>
        <li><strong>Happy path:</strong> the normal, expected use. Does it work?</li>
        <li><strong>Empty inputs:</strong> what happens if a field is blank?</li>
        <li><strong>Extreme inputs:</strong> very long text, weird characters, emojis.</li>
        <li><strong>Refresh:</strong> reload the page. Does your data survive?</li>
        <li><strong>Different sizes:</strong> shrink the browser or check on a phone.</li>
      </ol>
      <h3>The "logout" test</h3>
      <p>Log out of your app. Try to visit a page that only logged-in users should see. Are you politely redirected? Good. Can you see private data anyway? Bad. Very bad.</p>
      <div class="callout-warn"><strong>Never trust the first success.</strong> Once something works, try to <em>break</em> it. Bugs love complacency.</div>
    `,
  },
  {
    id: "deployment",
    number: "Ch. 12",
    title: "Deployment — Sending Your App Into The World",
    summary: "How your app goes from your laptop to a real internet address.",
    content: `
      <p>Deployment is the moment your creation stops being a private toy and becomes a public thing with a URL that friends can visit.</p>
      <h3>What "deploy" actually means</h3>
      <p>You upload the code to a service (like Vercel, Railway, or your builder platform), it runs it on their computers, and points a public web address at it.</p>
      <h3>Before you deploy — the pre-flight check</h3>
      <ul>
        <li>Test the app one more time end-to-end.</li>
        <li>Make sure no secret keys or passwords are inside the code — they belong in <em>environment variables</em>.</li>
        <li>Confirm your database is one that lives online, not on your laptop.</li>
        <li>Check the site loads on both desktop and mobile.</li>
      </ul>
      <h3>After you deploy</h3>
      <ul>
        <li>Open the live URL in a private browser window (no cached logins).</li>
        <li>Test the same five checks from Chapter 11.</li>
        <li>Share the URL with one friend before you announce anywhere public. Free QA.</li>
      </ul>
      <div class="callout-tip"><strong>Domain names:</strong> once you're happy, buy a real .com. It's usually £10–15 a year and makes the whole thing feel real.</div>
    `,
  },
  {
    id: "monetise",
    number: "Ch. 13",
    title: "Making Money From Your App",
    summary: "The honest options for turning your build into income.",
    content: `
      <p>Building the app is half the game. Getting paid is the other half. Here are the routes that actually work for small builders.</p>
      <h3>1. One-time purchase</h3>
      <p>Best for tools, ebooks, calculators, plug-ins. Simple. No ongoing support expectations.</p>
      <h3>2. Subscription (monthly / yearly)</h3>
      <p>Best for apps used repeatedly — dashboards, trackers, communities. Steadier income, but you must keep improving.</p>
      <h3>3. Free preview + paid unlock</h3>
      <p>Let people try 20% for free, then pay to unlock the rest. Excellent for content-heavy apps (like this book).</p>
      <h3>4. Ads</h3>
      <p>Free for users, revenue from clicks. Only makes real money at very large traffic. Consider carefully — it can cheapen the experience.</p>
      <h3>5. Consulting / done-for-you services</h3>
      <p>Your app becomes a portfolio piece. People pay you to build them one just like it.</p>
      <div class="callout-tip"><strong>Pricing rule:</strong> your first users are worth more as testimonials than as revenue. Charge, but charge a little; ask for feedback in return.</div>
      <h3>Payment tools you'll hear about</h3>
      <ul>
        <li><strong>Stripe</strong> — the standard for taking card payments. Trusted everywhere.</li>
        <li><strong>PayPal</strong> — still popular, especially internationally.</li>
        <li><strong>Lemon Squeezy / Gumroad</strong> — friendlier for digital products.</li>
      </ul>
    `,
  },
  {
    id: "common-traps",
    number: "Ch. 14",
    title: "Common Traps To Avoid",
    summary: "The mistakes almost every first-time AI builder makes.",
    content: `
      <ol>
        <li><strong>Asking for too much at once.</strong> "Build me an Uber for lawn-mowers with chat and payments." Break it down. Slice by slice.</li>
        <li><strong>Skipping GitHub.</strong> One bad afternoon can wipe out a fortnight of work. Don't.</li>
        <li><strong>Hardcoding secrets.</strong> Never paste an API key directly in your code. Use environment variables.</li>
        <li><strong>Ignoring errors.</strong> Red text is information, not decoration.</li>
        <li><strong>Not testing on a phone.</strong> Half your users will be on one.</li>
        <li><strong>Perfectionism.</strong> Shipping a modest app that works beats polishing a masterpiece that never launches.</li>
        <li><strong>Believing the AI blindly.</strong> Always sanity-check important outputs — numbers, links, prices, dates.</li>
        <li><strong>Forgetting the user.</strong> If your mum can't work out what to click first, redesign it.</li>
      </ol>
      <blockquote>"The person who ships wins. The person who tinkers forever tells themselves stories about winning."</blockquote>
    `,
  },
  {
    id: "prompt-recipes",
    number: "Ch. 15",
    title: "Prompt Recipes For Real Situations",
    summary: "Copy-paste starting points for the moments you'll actually face.",
    content: `
      <h3>Starting a new feature</h3>
      <pre><code>Before writing code, describe your plan in plain English.
List each file you'll create or change, and why.
Do not touch anything outside those files.
Once I approve, write the smallest working version first.</code></pre>
      <h3>Fixing a bug</h3>
      <pre><code>Here is the exact error and the file it points to.
Explain the cause in one paragraph.
Suggest the smallest fix that resolves it without changing behaviour elsewhere.
Then apply it and stop.</code></pre>
      <h3>Adding a design tweak</h3>
      <pre><code>Only change the visual style of the following component.
Keep behaviour and structure identical.
Match the existing dark cyan theme.</code></pre>
      <h3>Getting unstuck</h3>
      <pre><code>Stop coding. Explain, step by step, how the current flow works
from the moment the user clicks the button to the moment they see a result.
Point out anything that seems suspicious to you.</code></pre>
      <div class="callout-tip"><strong>Keep a swipe file:</strong> save the prompts that work for you. Reuse them shamelessly.</div>
    `,
  },
  {
    id: "talking-to-ai",
    number: "Ch. 16",
    title: "Talking To AI When You're Stuck",
    summary: "How to get out of the mud when nothing seems to work.",
    content: `
      <p>Every builder — every single one — hits a wall at some point. Here's how to climb it without throwing your laptop.</p>
      <h3>Step 1 — Name the problem out loud</h3>
      <p>Type it out as if you're explaining to a friend on the phone. Include: what you tried, what happened, and what you expected.</p>
      <h3>Step 2 — Give the AI the actual evidence</h3>
      <p>Paste the error. Paste the file. Screenshots help too. Do not describe from memory.</p>
      <h3>Step 3 — Ask for options, not a verdict</h3>
      <blockquote>"Give me three possible causes and how I'd tell them apart."</blockquote>
      <p>You'll learn more, faster, and often spot the real cause yourself.</p>
      <h3>Step 4 — Reset if needed</h3>
      <p>If the AI is going in circles, start a fresh conversation. Fresh context, fresh answer.</p>
      <div class="callout-warn"><strong>Rollback is your friend.</strong> If today's work made things worse, revert to yesterday's stable version and start again with a clearer plan.</div>
    `,
  },
  {
    id: "whats-next",
    number: "Ch. 17",
    title: "What's Next After Your First App",
    summary: "How to grow from 'I built a thing' to 'I run a thing.'",
    content: `
      <p>Your first app is live. Congratulations — genuinely. Most people never get this far. What now?</p>
      <h3>Get real users</h3>
      <ul>
        <li>Post about it in a community where people have the problem you solved.</li>
        <li>Ask five people to try it in front of you. Watch silently. You'll learn heaps.</li>
        <li>Fix the top complaint. Repeat.</li>
      </ul>
      <h3>Track just enough</h3>
      <p>Add a simple analytics tool (like Plausible or PostHog). Answer only two questions: <em>how many people came?</em> and <em>did they do the thing?</em> That's it.</p>
      <h3>Learn what you don't know</h3>
      <p>Now that you've built something real, terms you couldn't grasp before will click. Come back to earlier chapters — they'll read differently.</p>
      <h3>Build the next one</h3>
      <p>The second app takes half the time. The third takes a quarter. The tenth, you'll do in an afternoon.</p>
      <blockquote>"You are now, officially, a builder. The rest is practice."</blockquote>
    `,
  },
  {
    id: "glossary",
    number: "Ch. 18",
    title: "Glossary — The Words They All Use",
    summary: "Plain-English translations of the jargon.",
    content: `
      <p>Bookmark this page. Come back whenever a word looks unfamiliar.</p>
      <h3>A–E</h3>
      <ul>
        <li><strong>API</strong> <span class="text-slate-500">(Application Programming Interface)</span> — a way for two apps to talk to each other. Like a waiter passing orders between you and the kitchen.</li>
        <li><strong>Backend</strong> — the invisible engine of an app that runs on a server.</li>
        <li><strong>Bug</strong> — a mistake in the code that makes it misbehave.</li>
        <li><strong>Cache</strong> — a temporary stash of information kept close by to load faster.</li>
        <li><strong>CORS</strong> <span class="text-slate-500">(Cross-Origin Resource Sharing)</span> — the polite rule that stops random websites talking to your backend.</li>
        <li><strong>Commit</strong> — a saved snapshot of your project in GitHub.</li>
        <li><strong>Database</strong> — where an app remembers things long-term.</li>
        <li><strong>Debug</strong> — the act of finding and squashing bugs.</li>
        <li><strong>Deploy</strong> — put your app live on the internet.</li>
        <li><strong>Environment variable</strong> — a secret note (like a password) kept outside the code.</li>
      </ul>
      <h3>F–M</h3>
      <ul>
        <li><strong>Frontend</strong> — the visible part of an app you tap and see.</li>
        <li><strong>Framework</strong> — a pre-built set of tools that saves you re-inventing the wheel.</li>
        <li><strong>Git</strong> — the underlying tool that GitHub is built on.</li>
        <li><strong>HTML / CSS / JS</strong> <span class="text-slate-500">(HyperText Markup Language / Cascading Style Sheets / JavaScript)</span> — the three main languages of the web.</li>
        <li><strong>Hash</strong> — a one-way scramble used to store passwords safely.</li>
        <li><strong>JSON</strong> <span class="text-slate-500">(JavaScript Object Notation)</span> — a common format for passing data between apps. Looks like curly braces.</li>
        <li><strong>Library</strong> — pre-written code you can borrow instead of writing yourself.</li>
        <li><strong>LLM</strong> — Large Language Model. The kind of AI that writes and understands text.</li>
      </ul>
      <h3>N–Z</h3>
      <ul>
        <li><strong>MVP</strong> <span class="text-slate-500">(Minimum Viable Product)</span> — the smallest version that's worth showing.</li>
        <li><strong>Prompt</strong> — the instruction you give an AI.</li>
        <li><strong>Repository</strong> (repo) — one project's folder in GitHub.</li>
        <li><strong>Server</strong> — a computer on the internet that runs your app.</li>
        <li><strong>SDK</strong> <span class="text-slate-500">(Software Development Kit)</span> — a ready-made toolbox for using a service.</li>
        <li><strong>SQL / NoSQL</strong> <span class="text-slate-500">(Structured Query Language / &quot;Not only SQL&quot;)</span> — two families of database.</li>
        <li><strong>Stack</strong> — the collection of tools your app is built with.</li>
        <li><strong>Token</strong> — a small string that proves who you are (or counts AI usage).</li>
        <li><strong>UI / UX</strong> <span class="text-slate-500">(User Interface / User Experience)</span> — how the app looks / how it feels to use.</li>
        <li><strong>URL</strong> <span class="text-slate-500">(Uniform Resource Locator)</span> — the address of a page on the internet.</li>
      </ul>
    `,
  },
];

// Total logical "pages" = cover + toc + chapters + index + back
export const PAGES = [
  { kind: "cover", id: "cover", title: "Cover" },
  { kind: "toc", id: "toc", title: "Contents" },
  ...CHAPTERS.map((c) => ({ kind: "chapter", id: c.id, title: c.title, chapter: c })),
  { kind: "index", id: "index", title: "Index" },
  { kind: "back", id: "back", title: "The End" },
];

// Index entries: term -> chapter id
export const INDEX_ENTRIES = [
  { term: "AI (working with)", chapterId: "meet-ai" },
  { term: "API", chapterId: "glossary" },
  { term: "Backend", chapterId: "what-is-an-app" },
  { term: "Bugs, dealing with", chapterId: "debugging" },
  { term: "Cache", chapterId: "glossary" },
  { term: "Code languages, overview", chapterId: "code-languages" },
  { term: "Commit (GitHub)", chapterId: "github" },
  { term: "CORS errors", chapterId: "debugging" },
  { term: "CSS", chapterId: "code-languages" },
  { term: "Data, where it lives", chapterId: "data-storage" },
  { term: "Database (SQL vs NoSQL)", chapterId: "data-storage" },
  { term: "Debugging mindset", chapterId: "debugging" },
  { term: "Deployment", chapterId: "deployment" },
  { term: "Environment variables", chapterId: "common-traps" },
  { term: "Errors, reading", chapterId: "debugging" },
  { term: "First build, walk-through", chapterId: "first-build" },
  { term: "Frontend", chapterId: "what-is-an-app" },
  { term: "GitHub — why to use it", chapterId: "github" },
  { term: "Glossary of terms", chapterId: "glossary" },
  { term: "HTML", chapterId: "code-languages" },
  { term: "JavaScript", chapterId: "code-languages" },
  { term: "JSON", chapterId: "glossary" },
  { term: "Local vs online storage", chapterId: "local-vs-online" },
  { term: "LLM (Large Language Model)", chapterId: "glossary" },
  { term: "Making money / monetisation", chapterId: "monetise" },
  { term: "MVP", chapterId: "glossary" },
  { term: "Passwords / hashing", chapterId: "data-storage" },
  { term: "PayPal", chapterId: "monetise" },
  { term: "Prompting, the recipe", chapterId: "prompting" },
  { term: "Prompt recipes", chapterId: "prompt-recipes" },
  { term: "Python", chapterId: "code-languages" },
  { term: "Rollback strategy", chapterId: "talking-to-ai" },
  { term: "Server", chapterId: "local-vs-online" },
  { term: "SQL", chapterId: "code-languages" },
  { term: "Stripe", chapterId: "monetise" },
  { term: "Subscription pricing", chapterId: "monetise" },
  { term: "Testing checklist", chapterId: "testing" },
  { term: "Traps to avoid", chapterId: "common-traps" },
  { term: "URL", chapterId: "glossary" },
  { term: "User feedback", chapterId: "whats-next" },
  { term: "What's next", chapterId: "whats-next" },
];
